import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, NgForm } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { SysReturnList } from '../../../interfaces/SysReturnList.interfaces';
import { SysReturnListService } from '../../../services/sys-return-list.service';
import {
  ReturnRequestService,
  ClientInfo,
  BrandInfo,
  ClientBrandsMap
} from '../../../services/return-request.service';

type ReturnSettingsTab = 'list' | 'new' | 'edit';

@Component({
  selector: 'app-return-settings-popup',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslateModule],
  templateUrl: './return-settings-popup.component.html',
  styleUrl: './return-settings-popup.component.css'
})
export class ReturnSettingsPopupComponent implements OnInit, OnChanges {
  @Input() visible: boolean = false;
  @Output() closed = new EventEmitter<void>();

  @ViewChild('f') ngForm?: NgForm;

  activeTab: ReturnSettingsTab = 'list';

  items: SysReturnList[] = [];
  loading = false;
  saving = false;
  errorMessage = '';
  successMessage = '';

  selected: SysReturnList | null = null;
  formModel: SysReturnList = this.emptyModel();

  searchTerm: string = '';

  /** Lista completa dei clienti restituita dal backend */
  clients: ClientInfo[] = [];
  /** Lista completa dei brand restituita dal backend */
  brands: BrandInfo[] = [];
  /** Mapping clientCode -> brandCodes (raw dal backend) */
  clientBrandsMap: ClientBrandsMap = {};
  /** Indici di lookup per accesso rapido */
  private clientByCode: Map<string, ClientInfo> = new Map();
  private brandByCode: Map<string, BrandInfo> = new Map();
  loadingClients = false;

  constructor(
    private service: SysReturnListService,
    private returnRequestService: ReturnRequestService
  ) {}

  ngOnInit(): void {
    document.addEventListener('keydown', this.handleKeyDown);
    if (this.visible) {
      this.loadAll();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['visible'] && this.visible) {
      this.activeTab = 'list';
      this.selected = null;
      this.resetMessages();
      this.loadAll();
      this.loadClientsAndBrands();
    }
  }

  private handleKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Escape' && this.visible) {
      this.close();
    }
  };

  close(): void {
    document.removeEventListener('keydown', this.handleKeyDown);
    this.closed.emit();
  }

  setActiveTab(tab: ReturnSettingsTab): void {
    if (tab === 'edit' && !this.selected) {
      return;
    }
    this.activeTab = tab;
    this.resetMessages();
    if (tab === 'new') {
      this.formModel = this.emptyModel();
    } else if (tab === 'edit' && this.selected) {
      this.formModel = { ...this.selected };
    }
  }

  loadAll(): void {
    this.loading = true;
    this.resetMessages();
    this.service.getAll().subscribe({
      next: (data) => {
        this.items = data || [];
        this.loading = false;
      },
      error: (err) => {
        this.errorMessage = 'Errore nel caricamento dei record: ' + (err?.error?.message || err?.message || '');
        this.loading = false;
      }
    });
  }

  /** Carica clienti e brand da OPR_CLIENT_BRANDS e li indicizza per il cascading select. */
  loadClientsAndBrands(): void {
    this.loadingClients = true;
    this.returnRequestService.getClientsAndBrands().subscribe({
      next: (resp) => {
        this.clients = (resp?.clients || []).slice()
          .sort((a, b) => (a.clientName || a.clientCode).localeCompare(b.clientName || b.clientCode));
        this.brands = resp?.brands || [];
        this.clientBrandsMap = resp?.clientBrands || {};

        // Indici per lookup O(1)
        this.clientByCode.clear();
        for (const c of this.clients) {
          if (c.clientCode) this.clientByCode.set(c.clientCode, c);
        }
        this.brandByCode.clear();
        for (const b of this.brands) {
          if (b.brandCode) this.brandByCode.set(b.brandCode, b);
        }

        this.loadingClients = false;
      },
      error: (err) => {
        this.loadingClients = false;
        this.errorMessage = 'Errore caricamento clienti/brand: ' + (err?.error?.message || err?.message || '');
      }
    });
  }

  /** Lista brand per il cliente attualmente selezionato nel form. */
  brandsForSelectedClient(): BrandInfo[] {
    const c = this.formModel.returnClientCode;
    if (!c) return [];
    const codes = this.clientBrandsMap[c] || [];
    return codes
      .map(code => this.brandByCode.get(code))
      .filter((b): b is BrandInfo => !!b);
  }

  /** Restituisce il nome del cliente dato il suo codice (fallback al codice). */
  getClientName(code?: string | null): string {
    if (!code) return '-';
    const c = this.clientByCode.get(code);
    return c?.clientName || code;
  }

  /** Restituisce l'acronimo del brand dato il codice (fallback a name o codice). */
  getBrandName(_clientCode: string | null | undefined, brandCode?: string | null): string {
    if (!brandCode) return '-';
    const b = this.brandByCode.get(brandCode);
    return b?.brandAcronym || b?.brandName || brandCode;
  }

  /** Quando cambia il cliente, resetto il brand se non più valido. */
  onClientChange(): void {
    const list = this.brandsForSelectedClient();
    const current = this.formModel.returnBrandCode;
    if (current && !list.some(b => b.brandCode === current)) {
      this.formModel.returnBrandCode = '';
    }
  }

  selectForEdit(item: SysReturnList): void {
    this.selected = item;
    this.formModel = { ...item };
    this.setActiveTab('edit');
  }

  insert(): void {
    if (this.ngForm && this.ngForm.invalid) {
      this.ngForm.control.markAllAsTouched();
      this.errorMessage = 'Compila tutti i campi obbligatori';
      return;
    }
    this.saving = true;
    this.resetMessages();
    this.service.insert(this.formModel).subscribe({
      next: (created) => {
        this.items = [created, ...this.items];
        this.successMessage = 'Record creato con successo';
        this.formModel = this.emptyModel();
        this.saving = false;
        this.setActiveTab('list');
      },
      error: (err) => {
        this.errorMessage = 'Errore inserimento: ' + (err?.error?.message || err?.message || '');
        this.saving = false;
      }
    });
  }

  update(): void {
    if (this.ngForm && this.ngForm.invalid) {
      this.ngForm.control.markAllAsTouched();
      this.errorMessage = 'Compila tutti i campi obbligatori';
      return;
    }
    if (this.formModel.returnId == null) {
      this.errorMessage = 'returnId mancante per update';
      return;
    }
    this.saving = true;
    this.resetMessages();
    this.service.update(this.formModel.returnId, this.formModel).subscribe({
      next: (updated) => {
        this.items = this.items.map(i => i.returnId === updated.returnId ? updated : i);
        this.selected = updated;
        this.successMessage = 'Record aggiornato con successo';
        this.saving = false;
        this.setActiveTab('list');
      },
      error: (err) => {
        this.errorMessage = 'Errore aggiornamento: ' + (err?.error?.message || err?.message || '');
        this.saving = false;
      }
    });
  }

  filteredItems(): SysReturnList[] {
    const term = this.searchTerm.trim().toLowerCase();
    if (!term) return this.items;
    return this.items.filter(i =>
      Object.values(i).some(v => v != null && v.toString().toLowerCase().includes(term))
    );
  }

  private emptyModel(): SysReturnList {
    return {
      returnId: 0,
      returnClientCode: '',
      returnBrandCode: '',
      returnPalletType: '',
      returnPalletName: '',
      returnPalletLength: undefined,
      returnPalletWidth: undefined,
      returnPalletHeight: undefined,
      returnPalletWeight: undefined,
      returnPalletVolume: undefined,
      returnType: '',
      returnName: '',
      returnDescription: '',
      returnStatus: 'ACTIVE',
      returnCreatedAt: ''
    };
  }

  private resetMessages(): void {
    this.errorMessage = '';
    this.successMessage = '';
  }
}
